import { useEffect, useState, useRef } from 'react';
import { flushSync } from 'react-dom';
import {
  Box,
  Flex,
  ErrorMessage,
  Text,
  useDisclosure,
  LoadingSpinner,
  Alert,
  useToast
} from '_shared/designSystem/components';
import { useSetRecoilState } from 'recoil';
import { mobileHeaderTextState } from '_shared/globalState/atoms';
import { useMatch, useNavigate, useSearch } from '@tanstack/react-location';
import StickyHeader from '_shared/components/StickyHeader';
import isEmpty from 'lodash/isEmpty';
import CollectionForm from './CollectionForm';
import ServingSideGraphic from './ServingSideGraphic';
import { MatchScore } from './matchScore/MatchScore';
import { collectService } from 'collect/services/collectService';
import { playerService } from '_shared/services/playerService';
import { MatchInfoHeader } from 'match/components/MatchInfoHeader';
import { collectFieldType, initialCollectionFormData } from '_shared/constants/collectionTypes';
import NextPointModal from './NextPointModal';
import { useMutation, useQuery } from 'react-query';
import { AblyUpdater } from './AblyUpdater';
import { applyCollectFieldToFormData, buildPostCollectVariables } from 'collect/util/collectFormStateUtil';

export default function Collect() {
  const [formData, setFormData] = useState(initialCollectionFormData);
  const [submittedRallyLength, setSubmittedRallyLength] = useState(false);
  const [rallyLengthCount, setRallyLengthCount] = useState(0);
  const [liveScore, setLiveScore] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [onlineStatus, setOnlineStatus] = useState(true);
  const [set, setSet] = useState(null);
  const [game, setGame] = useState(null);
  const [point, setPoint] = useState(null);
  const [pointJoinKey, setPointJoinKey] = useState(null);
  const [flags, setFlags] = useState([]);

  const [showNextPointLoading, setShowNextPointLoading] = useState(false);
  const [showNextPointModalLoading, setShowNextPointModalLoading] = useState(false);
  const [showResetLoading, setShowResetLoading] = useState(false);

  const formDataRef = useRef(formData);
  const submittedRallyLengthRef = useRef(submittedRallyLength);
  // Only sync local shots counter when the point identity changes (not on poll refetch),
  // so unsaved shot edits are not wiped every 5s.
  const pointSyncKeyRef = useRef(null);

  useEffect(() => {
    formDataRef.current = formData;
  }, [formData]);

  useEffect(() => {
    submittedRallyLengthRef.current = submittedRallyLength;
  }, [submittedRallyLength]);

  const {
    params: { matchId }
  } = useMatch();
  const searchParams = useSearch();

  const setMobileHeaderText = useSetRecoilState(mobileHeaderTextState);
  const { isOpen, onClose, onOpen } = useDisclosure();
  const navigate = useNavigate();
  const toast = useToast();

  useEffect(() => {
    if (searchParams.set) setSet(searchParams.set);
    if (searchParams.game) setGame(searchParams.game);
    if (searchParams.point) setPoint(searchParams.point);
  }, [searchParams]);

  const {
    isLoading: getCollectIsLoading,
    error: getCollectError,
    data,
    isSuccess: getCollectIsSuccess,
    refetch: refetchPointData
  } = useQuery(
    ['collectService_getPointData', matchId, set, game, point],
    () => collectService.getPointData({ matchId, set, game, point }),
    { enabled: !!set, refetchInterval: 5000 }
  );

  useEffect(() => {
    if (!getCollectIsSuccess) return;

    setLiveScore(data?.live_score);
    setPointJoinKey(data?.point_join_key);

    if (!data?.point_data?.manual) return;

    const rallyLength = data.point_data.manual.rally_length ?? 0;
    const manual = { ...data.point_data.manual, rally_length: rallyLength };
    setFormData(manual);

    const syncKey = `${matchId}|${set}|${game}|${point}|${data?.point_join_key}`;
    if (pointSyncKeyRef.current !== syncKey) {
      pointSyncKeyRef.current = syncKey;
      setRallyLengthCount(rallyLength);
      setSubmittedRallyLength(rallyLength > 0);
    } else if (rallyLength > 0) {
      setSubmittedRallyLength(true);
    }
  }, [getCollectIsSuccess, data, matchId, set, game, point]);

  const { mutate: submitCollectMutate } = useMutation(collectService.postCollect, {
    onMutate: (variables) => {
      const previousFormData = { ...formDataRef.current };
      const previousSubmittedRallyLength = submittedRallyLengthRef.current;
      const nextFormData = applyCollectFieldToFormData(formDataRef.current, variables.fieldName, variables.value);
      flushSync(() => {
        setFormData(nextFormData);
        if (variables.fieldName === collectFieldType.RALLY_LENGTH) {
          setSubmittedRallyLength(true);
        }
      });
      formDataRef.current = nextFormData;
      submittedRallyLengthRef.current =
        variables.fieldName === collectFieldType.RALLY_LENGTH ? true : submittedRallyLengthRef.current;
      return { previousFormData, previousSubmittedRallyLength };
    },
    onSuccess: (data, variables) => {
      if (variables.fieldName === collectFieldType.RALLY_LENGTH) {
        setSubmittedRallyLength(true);
      }
    },
    onError: (error, variables, context) => {
      if (context?.previousFormData) {
        flushSync(() => {
          setFormData(context.previousFormData);
          setSubmittedRallyLength(context.previousSubmittedRallyLength);
        });
        formDataRef.current = context.previousFormData;
        submittedRallyLengthRef.current = context.previousSubmittedRallyLength;
      }
      if (error?.response?.status === 403) {
        setErrorMessage('You do not have permission to perform this action');
      } else {
        setErrorMessage('An error has occured submitting the item. Please try again');
      }
    }
  });

  const { mutate: submitCannotCollectMutate } = useMutation(collectService.postCannotCollect, {
    onSuccess: () => {
      handleNextPointContinue();
    },
    onError: (error) => {
      if (error?.response?.status === 403) {
        setErrorMessage('You do not have permission to perform this action');
      } else {
        setErrorMessage('An error has occured when submitting. Please try again');
      }
    }
  });

  const { mutate: resetPointMutate } = useMutation(collectService.resetPointData, {
    onMutate: () => {
      setShowResetLoading(true);
    },
    onSuccess: () => {
      // Refresh the page to show the point in uncollected state
      window.location.reload();
    },
    onError: (error) => {
      if (error?.response?.status === 403) {
        setErrorMessage('You do not have permission to reset this point');
      } else {
        setErrorMessage('An error occurred while resetting the point data');
      }
    },
    onSettled: () => {
      setShowResetLoading(false);
    }
  });

  const { mutate: nextPointMutate } = useMutation(collectService.getNextPoint, {
    onMutate: () => {
      // Set isIsNextPointLoading to true right before the request starts
      setShowNextPointLoading(true);
    },
    onSuccess: (data) => {
      const flagArray = [];
      if (data?.data_incomplete) {
        flagArray.push('Data submitted for this point is incomplete.');
      }
      if (data?.rally_length_issue) {
        flagArray.push('There may be an issue with the rally length for this point based on the point winner.');
      }

      if (!isEmpty(flagArray)) {
        setFlags(flagArray);
        onOpen();
      } else {
        resetData(data);
      }
    },
    onError: (error) => {
      if (error.response && error.response.data.error.includes('Point data not found for point join key')) {
        setErrorMessage('Point has not yet finished, please try again after completion of point');
      } else {
        setErrorMessage('An error occurred when moving to the next point. Please try again');
      }
    },
    onSettled: () => {
      // Set isIsNextPointLoading to false after the request has either succeeded or failed
      setShowNextPointLoading(false);
    }
  });

  const { mutate: nextPointNoCheckMutate } = useMutation(collectService.getNextPointNoCheck, {
    onSuccess: (data) => {
      onClose();
      resetData(data);
    },
    onError: () => {
      onClose();
      setErrorMessage('An error occured when moving to the next point. Please try again');
    },
    onSettled: () => {
      setShowNextPointModalLoading(false);
    }
  });

  const { mutate: updateHandednessMutate } = useMutation(playerService.updatePlayerHandedness, {
    onSuccess: () => {
      toast({
        title: 'Handedness updated successfully',
        status: 'success',
        duration: 3000,
        isClosable: true
      });
      // Refetch the data to update the UI
      refetchPointData();
    },
    onError: (error) => {
      toast({
        title: 'Failed to update handedness',
        description: error?.response?.data?.message || 'An error occurred while updating handedness',
        status: 'error',
        duration: 5000,
        isClosable: true
      });
    }
  });

  const players = `${data?.match_info?.player1Name} v ${data?.match_info?.player2Name}`;

  useEffect(() => {
    if (!isEmpty(players)) {
      setMobileHeaderText(`Collect - ${players}`);
    }
  }, [setMobileHeaderText, players]);

  useEffect(() => {
    window.addEventListener('offline', () => {
      setOnlineStatus(false);
    });
    window.addEventListener('online', () => {
      setOnlineStatus(true);
    });

    return () => {
      window.removeEventListener('offline', () => {
        setOnlineStatus(false);
      });
      window.removeEventListener('online', () => {
        setOnlineStatus(true);
      });
    };
  }, []);

  const submitCollectionItem = (fieldName, value) => {
    const dataToSubmit = buildPostCollectVariables(
      formData,
      rallyLengthCount,
      matchId,
      pointJoinKey,
      set,
      game,
      point,
      fieldName,
      value
    );
    submitCollectMutate(dataToSubmit);
  };

  const submitCannotCollectItem = () => {
    const dataToSubmit = {
      matchId,
      pointJoinKey,
      set,
      game,
      point
    };
    submitCannotCollectMutate(dataToSubmit);
  };

  const handleResetPoint = () => {
    const dataToSubmit = {
      matchId,
      pointJoinKey,
      set,
      game,
      point
    };
    resetPointMutate(dataToSubmit);
  };

  const updateFormData = (field, value) => {
    setFormData((prev) => applyCollectFieldToFormData(prev, field, value));
  };

  const handleNextOrSubmit = async () => {
    nextPointMutate({ matchId, set, game, point, pointJoinKey });
  };

  const handleNextPointContinue = async () => {
    setShowNextPointModalLoading(true);
    nextPointNoCheckMutate({ matchId, set, game, point, pointJoinKey });
  };

  const resetData = (nextPoint) => {
    // Force the next point-data apply to re-sync shots (needed when next point is the same, e.g. match end).
    pointSyncKeyRef.current = null;
    const samePoint = nextPoint?.set === set && nextPoint?.game === game && nextPoint?.point === point;
    setFormData(initialCollectionFormData);
    setSubmittedRallyLength(false);
    setRallyLengthCount(0);
    setPointJoinKey(nextPoint?.point_join_key);
    navigate({
      search: (old) => ({ ...old, set: nextPoint?.set, game: nextPoint?.game, point: nextPoint?.point })
    });
    if (samePoint) {
      refetchPointData();
    }
  };

  const handleHandednessUpdate = (playerId, handedness) => {
    updateHandednessMutate({ matchId, playerId, handedness });
  };

  if (getCollectError)
    return (
      <>
        <StickyHeader text="Collect Data" />
        <ErrorMessage message="Unable to retrieve match info, please try refreshing the page." />
      </>
    );

  if (getCollectIsLoading) return <LoadingSpinner />;

  return (
    <Box>
      <AblyUpdater matchId={matchId} matchStatus={data?.live_score?.match_status} updateDataFunction={setLiveScore} />
      <Flex justify="center" w="100%">
        <Flex px={5} mt={2} gap={7} direction="column" maxW="2200px" pb={10} align="center" w="100%">
          <MatchInfoHeader
            subheading="Collect"
            matchInfo={data?.match_info}
            onHandednessUpdate={handleHandednessUpdate}
          />
          {!onlineStatus && (
            <Alert
              message="You look to be having internet issues. Some features may not work correctly"
              status="warning"
            />
          )}
          {errorMessage && <ErrorMessage message={errorMessage} />}
          <Flex>
            <ServingSideGraphic point={point} />
            <ScoresContainer
              collectingScoreData={data?.collecting_score}
              liveScoreData={liveScore}
              player1Name={data?.match_info?.player1Name}
              player2Name={data?.match_info?.player2Name}
              player1Handedness={data?.match_info?.player1Handedness}
              player2Handedness={data?.match_info?.player2Handedness}
              player1Id={data?.match_info?.player1_id}
              player2Id={data?.match_info?.player2_id}
              onHandednessUpdate={handleHandednessUpdate}
              videoUrl={data?.match_info?.video_url}
            />
          </Flex>
          <Text fontSize="sm">
            {`Set ${set} - Game ${game} - Point ${point} - PJK ${pointJoinKey}`}
            {data?.collecting_for?.point}
          </Text>
          <CollectionForm
            formData={formData}
            updateFormData={updateFormData}
            player1Name={data?.match_info?.player1Name}
            player2Name={data?.match_info?.player2Name}
            submitCollectionItem={submitCollectionItem}
            submitCannotCollectItem={submitCannotCollectItem}
            handleNextOrSubmit={handleNextOrSubmit}
            reviewing={false}
            submittedRallyLength={submittedRallyLength}
            rallyLengthCount={rallyLengthCount}
            setRallyLengthCount={setRallyLengthCount}
            showNextPointLoading={showNextPointLoading}
            handleResetPoint={handleResetPoint}
            showResetLoading={showResetLoading}
          />
          <NextPointModal
            handleContinue={handleNextPointContinue}
            isOpen={isOpen}
            onClose={onClose}
            flags={flags}
            showNextPointModalLoading={showNextPointModalLoading}
          />
        </Flex>
      </Flex>
    </Box>
  );
}

const ScoreBox = ({ children, label }) => (
  <Box borderColor="grey.300" borderWidth="1px" p={5}>
    <Text textAlign="center" fontSize="md" fontWeight="semibold" mb={1}>
      {label}
    </Text>
    {children}
  </Box>
);

const Scores = ({
  collectingScoreData,
  liveScoreData,
  player1Name,
  player2Name,
  player1Handedness,
  player2Handedness,
  player1Id,
  player2Id,
  onHandednessUpdate
}) => (
  <Flex flexWrap="wrap" gap="30px">
    <ScoreBox label="Live Score">
      {liveScoreData?.latest_l1 && (
        <Text textAlign="center" fontSize="md" fontWeight="semibold" mb={1}>
          {liveScoreData?.latest_l1}
        </Text>
      )}
      <MatchScore
        data={liveScoreData}
        player1Name={player1Name}
        player2Name={player2Name}
        player1Handedness={player1Handedness}
        player2Handedness={player2Handedness}
        player1Id={player1Id}
        player2Id={player2Id}
        onHandednessUpdate={onHandednessUpdate}
      />
    </ScoreBox>
    <ScoreBox label="Collecting Score">
      <MatchScore
        data={collectingScoreData}
        player1Name={player1Name}
        player2Name={player2Name}
        player1Handedness={player1Handedness}
        player2Handedness={player2Handedness}
        player1Id={player1Id}
        player2Id={player2Id}
        onHandednessUpdate={onHandednessUpdate}
      />
    </ScoreBox>
  </Flex>
);

const ScoresContainer = ({
  collectingScoreData,
  liveScoreData,
  player1Name,
  player2Name,
  player1Handedness,
  player2Handedness,
  player1Id,
  player2Id,
  onHandednessUpdate,
  videoUrl
}) => (
  <>
    <Flex flexWrap="wrap" gap={5}>
      <Flex>
        <Scores
          collectingScoreData={collectingScoreData}
          liveScoreData={liveScoreData}
          player1Name={player1Name}
          player2Name={player2Name}
          player1Handedness={player1Handedness}
          player2Handedness={player2Handedness}
          player1Id={player1Id}
          player2Id={player2Id}
          onHandednessUpdate={onHandednessUpdate}
        />
      </Flex>
    </Flex>
  </>
);
